// ============================================================================
// aviso-pedido-web.tsx — "llegó un pedido pagado por internet".
//
// Cuando Bold confirma un pago del ecommerce, el POS crea el pedido y emite
// `pedido_creado` con origen 'ecommerce'. Esta franja lo muestra arriba de
// cualquier módulo y NO se va sola: un pedido web ya está pagado y nadie del
// local lo tomó, así que alguien tiene que verlo. Se cierra con "Visto".
//
// Sólo muestra los de la sede que se está mirando.
// ============================================================================
import { useState } from 'react';
import { CheckCircle2, X } from 'lucide-react';
import { useRealtime } from '../hooks/use-realtime';
import { useBranch } from '../context/branch-context';
import { formatMoneyIntl } from '@/lib/moneda';
import { useTextos } from '../context/regional-context';
import { textosPedidos } from '../../i18n/modulos/pedidos';

interface AvisoWeb { id: number; referencia: string; total: number; sedeId: number | null; hora: string }

// Con COP: idéntico al Intl de siempre. Otra moneda: la de la instalación.
const pesos = (n: number) => formatMoneyIntl(n || 0, { maximumFractionDigits: 0 });

export function AvisoPedidoWeb() {
  const { currentBranchId, sedes } = useBranch();
  const [avisos, setAvisos] = useState<AvisoWeb[]>([]);
  const tx = useTextos(textosPedidos);

  useRealtime(['pedido_creado'], (ev) => {
    const d = ev.data || {};
    if (d.origen !== 'ecommerce' || !d.referencia) return;
    setAvisos(a => a.some(x => x.id === d.id) ? a : [...a, {
      id: d.id, referencia: d.referencia, total: Number(d.total) || 0,
      sedeId: d.sede_id ?? null,
      hora: new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }),
    }]);
  });

  const visibles = avisos.filter(a => a.sedeId == null || currentBranchId == null || a.sedeId === currentBranchId);
  if (!visibles.length) return null;

  return (
    <div className="space-y-2 px-4 pt-3">
      {visibles.map(a => (
        <div key={a.id} role="status"
             className="flex items-center gap-3 rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-3 shadow-sm">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-emerald-900">
              {tx('pagoConfirmadoBold', { ref: a.referencia })}
            </div>
            <div className="text-xs text-emerald-800">
              {pesos(a.total)}{tx('yaEntroCocina')}
              {' · '}{sedes.find(s => s.id === a.sedeId)?.nombre || tx('sede')} · {a.hora}
            </div>
          </div>
          <button onClick={() => setAvisos(x => x.filter(y => y.id !== a.id))}
                  className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
            <X className="h-3.5 w-3.5" aria-hidden /> {tx('visto')}
          </button>
        </div>
      ))}
    </div>
  );
}
